/* global sampleYear */

import { Style, Fill, Stroke, Circle, Text } from '../node_modules/ol/style';

import StyleControls from './StyleControls.js';
import {hexToRgb} from './util.js';
import polygonArea from './util/polygonArea.js';
import formatDist from './util/formatDist.js';
import {map} from './index.js';
import calcLength from './calcLength.js';
import createElementPlus from './util/createElementPlus.js'

var createTextStyle;
var setStylesUI;

/**
 * LayerStyle Constructor
 * @param {String} name of ol layer - just used in redraw()
 * @param {Object} override default styles
 * @return {Object} the object reveals the public method "styleFunc"
 *
 * instantiated in Draw.js and PROBABLY SHOULD ONLY BE INSTANTIATED ONCE
 * provides styleFunc() called by ol as each feature is rendered
 * provides setStylesUI() - user interface for setting default layer styles called only in:
 *    cpc/htdocs/mapserver/etc/conf.js and zones-conf.js
 *
 */

export default function(olLayerName, styles) {
  styles = styles || {};  // eslint-disable-line
  var attributeToLabel = styles.attributeToLabel || ['label'];

  // lines
  var strokeRGB = styles.strokeRGB || '24, 24, 24';
  var strokeOpacity = styles.strokeOpacity || '0.8';
  var strokeRGBA = 'rgba(' + strokeRGB + ', ' + strokeOpacity + ')';
  var strokeWidth = styles.strokeWidth || 2;
  var lineDash = styles.lineDash || null;

  // polygons
  var outlineRGB = styles.outlineRGB || '24, 24, 24';
  var outlineOpacity = styles.outlineOpacity || '0.8';
  var outlineRGBA = 'rgba(' + outlineRGB + ', ' + outlineOpacity + ')';
  var outlineWidth = styles.outlineWidth || 2;
  var fillRGB = styles.fillRGB || '255, 255, 255';
  var fillOpacity = styles.fillOpacity || '0.5';
  var fillRGBA = 'rgba(' + fillRGB + ', ' + fillOpacity + ')';

  // fonts
  var fontWeight = styles.fontWeight || 'normal';
  var fontSize = styles.fontSize || '14pt';
  var fontFamily = styles.fontFamily || 'sans-serif';
  var fontColor = styles.fontColor || '#000000';
  var fontOutlineColor = styles.fontOutlineColor || '#aaaaaa';
  var fontOutlineWidth = styles.fontOutlineWidth || 0;
  // var labelMaxRes = styles.labelMaxRes || 100;
  var overflow = styles.overflow || false;

  var styleControls = new StyleControls({
    prefix: 'layer-',
    // these are the "on change" handlers that apply the user's selection to the layer
    font: function() {
      var fontCharacteristic = this.id.replace('layer-', '');
      if (fontCharacteristic === 'fontsize') {
        setFontSize(this.value);
      } else if (fontCharacteristic ==='fontfamily') {
        setFontFamily(this.value);
      } else if (fontCharacteristic ==='fontweight') {
        setFontWeight(this.value);
      } else if (fontCharacteristic ==='fontoutlinewidth') {
        setFontOutlineWidth(this.value);
      } else {
        console.warn(fontCharacteristic + ' override not yet configured for layers');
      }
    },
    linewidth: function() {
      setStrokeWidth(this.value);
    },
    lineopacity: function() {
      setStrokeRGBA({opacity: this.value});
    },
    outlinewidth: function() {
      setOutlineWidth(this.value);
    },
    outlineopacity: function() {
      setOutlineRGBA({opacity: this.value});
    },
    fillopacity: function() {
      setFillRGBA({opacity: this.value});
    },
    colorpicker: function(event) {  // eslint-disable-line no-unused-vars
      switch(this.id) {
        case 'layer-linecolor':
          setStrokeColor(this.value);
          break;
        case 'layer-outlinecolor':
          setOutlineColor(this.value);
          break;
        case 'layer-fillcolor':
          setFillColor(this.value);
          break;
        case 'layer-fontcolor':
          setFontColor(this.value);
          break;
        case 'layer-fontoutlinecolor':
          setFontOutlineColor(this.value);
          break;
      }
    }
  });

  function getStrokeRGB() { return 'rgb(' + strokeRGB + ')'; }
  function getStrokeWidth() { return strokeWidth; }
  function getStrokeOpacity() { return strokeOpacity; }
  function getFontSize() { return fontSize; }
  function getFillRGB() { return 'rgb(' + fillRGB + ')'; }
  function getOutlineRGB() { return 'rgb(' + outlineRGB + ')'; }
  function getOutlineWidth() { return outlineWidth; }
  function getFillOpacity() { return fillOpacity; }

  function redraw() {
    map.getLayerByTitle(olLayerName).getSource().dispatchEvent('change');
    let printx = map.getLayerByTitle('printx');
    if (printx) {
      printx.getSource().dispatchEvent('change');
    }
  }

  function setFillRGBA(params) {
    fillRGB = params.color || fillRGB;
    fillOpacity = params.opacity || fillOpacity;
    fillRGBA = 'rgba(' + fillRGB + ', ' + fillOpacity + ')';
    redraw();
  }
  function setStrokeRGBA(params) {
    strokeRGB = params.color || strokeRGB;
    strokeOpacity = params.opacity || strokeOpacity;
    strokeRGBA = 'rgba(' + strokeRGB + ', ' + strokeOpacity + ')';
    redraw();
  }
  function setStrokeWidth(w) {
    strokeWidth = w;
    redraw();
  }
  function setOutlineRGBA(params) {
    outlineRGB = params.color || outlineRGB;
    outlineOpacity = params.opacity || outlineOpacity;
    outlineRGBA = 'rgba(' + outlineRGB + ', ' + outlineOpacity + ')';
    redraw();
  }
  function setOutlineWidth(w) {
    outlineWidth = w;
    redraw();
  }
  function setStrokeColor(c) { setStrokeRGBA({color: hexToRgb(c)}); }
  function setOutlineColor(c) { setOutlineRGBA({color: hexToRgb(c)}); }
  function setFillColor(c) {
    setFillRGBA({ color: hexToRgb(c) });
    // it's confusing to the user if they set a fill color but don't see it take effect becasue opacity == 0
    if (fillOpacity == 0) {
      styleControls.setFillOpacity(0.5);
    }
  }
  function setFontWeight(w) {
    fontWeight = w;
    redraw();
  }
  function setFontSize(v) {
    fontSize = v;
    redraw();
  }
  function setFontFamily(f) {
    fontFamily = f;
    redraw();
  }
  function setFontColor(c) {
    fontColor = c;
    redraw();
  }
  function setFontOutlineColor(c) {
    fontOutlineColor = c;
    redraw();
  }
  function setFontOutlineWidth(w) {
    fontOutlineWidth = w;
    redraw();
  }

  // CHECK THIS - ONLY cpc!
  function addAttributeControl(attribute, group) {
    const fullAttrib = group ? group + '-' + attribute : attribute;
    const id = 'chk_' + fullAttrib;
    // var div = $('<div>');
    const div = createElementPlus('div')

    if (attribute.indexOf('_nematode-') !== 0 ) {
      //// jQuery:
      // div.append($('<input type="checkbox">')  // fires attribLabels.on('change') below
      //   .attr({
      //     id: id,
      //     checked: attributeToLabel.indexOf(attribute) > -1  // CHECK THIS
      //   }));
      div.append(createElementPlus('input', {
        type: 'checkbox',
        id: id,
        checked: attributeToLabel.indexOf(attribute) > -1  // CHECK THIS
      }))
    }

    if (attribute.indexOf('_soils-') === 0 ) {
      // div.append($('<input type="radio">')
      //   .attr({
      //     name: 'radio-thematic',
      //     value: fullAttrib
      //   })
      //   .change(function() { rwg.config.thematic.setThematicField(this.value) })
      // )
      div.append(createElementPlus('input', {
        type: 'radio',
        name: 'radio-thematic',
        value: fullAttrib,
        change: () => { rwg.config.thematic.setThematicField(this.value) }
      }))
    }

    if (attribute.indexOf('_nematode-') !== 0 ) {
      div.append(createElementPlus('label', {
        'for': id,
        innerHTML: attribute[0] === '_' ? attribute.substring(1) : attribute,  // remove leading underscore
      }))
    }

    return div
  }

  /* layer styles, attributes to label, attributes for thematic styling
     appends form to right-side "Drawing" tab
     called by SourceJSON success function - not ideal
     for consistency should export this and the attach it to rwg in index.js
     could this be a separate module?
   */
  // CHECK THIS - ONLY cpc!
  rwg.setStylesUI = function(attributes) {
    // let form = $('<form>', {id: "layer-style-controls"}).html('<h2 style="margin:0">Layer Styles</h2>');
    const form = createElementPlus('form', {
      id: "layer-style-controls",
      innerHTML:'<h2 style="margin:0">Layer Styles</h2>',
    })

    // there will pretty much always be attributes becasue this is only called after data is ajax loaded!
    if (attributes) {
      // let attribLabels = $('<div>', { id: "layer-attribute-controls"})
      const attribLabels = createElementPlus('div', {
        id: "layer-attribute-controls",
        // try putting the 'change' event listener here instead of below
      })

      // attributes to be label
      if (rwg.config.thematic) {
        // jQuery:
        // attribLabels.append('Check Box: Attributes to Label<br>Radio Button: Attribute for Thematic Styling<br>');
        attribLabels.innerHTML = 'Check Box: Attributes to Label<br>Radio Button: Attribute for Thematic Styling<br>'
      }

      attributes.forEach(attribute => {
        attribLabels.append(addAttributeControl(attribute));
      })

      /* // jQuery:
      attribLabels.append(
        $('<input type="checkbox">')
          .attr({
            id: 'chk__geom' ,
            checked: attributeToLabel.indexOf('_geom') > -1
          }),
        " Feature's Area or Length"  // eslint-disable-line quotes
      );
      */
      attribLabels.append(createElementPlus('input', {
        type: 'checkbox',
        id: 'chk__geom',
        checked: attributeToLabel.indexOf('_geom') > -1,
      }))
      attribLabels.append(document.createTextNode(`Feature's Area or Length`))

      attribLabels.addEventListener('change', function(evt) {
        // if (evt.target.type === 'checkbox') { // ignore radio buttons - why?
          let attrib = evt.target.id.replace('chk_', '');
          if (evt.target.checked) {
            attributeToLabel.push(attrib);
          } else {  // attribute labels is being turned off so remove from attributeToLabel Array
            let index = attributeToLabel.indexOf(attrib);
            if (index > -1) {
              attributeToLabel.splice(index, 1);
            }
          }
          redraw()
        // }
      })

      form.append(attribLabels);
    } else { console.warn('in setStylesUI() no attributes')}

    styleControls.setVals({
      fontsize: getFontSize(),
      fontweight: fontWeight,
      fontfamily: fontFamily,
      fontcolor: fontColor,
      fontoutlinecolor: fontOutlineColor,
      fontoutlinewidth: fontOutlineWidth,
      linecolor: getStrokeRGB(),
      lineopacity: getStrokeOpacity(),
      linewidth: getStrokeWidth(),
      outlinecolor: getOutlineRGB(),
      outlinewidth: getOutlineWidth(),
      fillcolor: getFillRGB(),
      fillopacity: getFillOpacity()
    });
    form.append(styleControls.controls);

    // $('#drawTab').append(form);
    document.querySelector('#drawTab').append(form)

  }

  // copied from http://openlayers.org/examples/vector-labels.html
  createTextStyle = function (feature, resolution) {
    // if (resolution > labelMaxRes) return null;
    var val, geom, distTotal, label = '', style;

    attributeToLabel.forEach(function(attrib) {
      val = '';
      if (attrib === '_geom') { // area for polygons or length for lines
        geom = feature.getGeometry();
        if (geom.getType().indexOf('Polygon') > -1) {
          val = geom.get('areaLabel') || polygonArea(geom);  // polygonArea(geom) returns an area label AND sets geom's areaLabel so that it won't have to be calculated next time
        } else if (geom.getType().indexOf('Line') > -1) {
          //  distTotal = geom.get('distTotal');
          if (!distTotal) {  // if geometry's 'distTotal' property isn't set then calculate & cache the value
            // DO NOT USE ol method geom.getLength() with epsg:3857 because it's cartesian
            // ol does provide spherical lengths and areas with  getLength(geom) and getArea(geom) which require: import {getArea, getLength} from '../node_modules/ol/sphere.js';
            // distTotal = (rwg.geodesic) ? calcLength(geom) : geom.getLength(); // calcLength() is rwg function, getLength() is ol3 function
            distTotal = calcLength(geom); // calcLength() is rwg function, getLength() is ol3 function
          }

          val = formatDist(distTotal);
        }
      } else if (attrib.indexOf('_') === 0) { // cpc specific _ underscore prefix indicates a layer w/ possibly one-to-many poly-to-attributes _soils _nematode
        // _soils
        if (attrib.indexOf("_soils") === 0) {
          let indexOfSpace = attrib.indexOf('-'); // Changed to hyphen 2020-04-02
          if (indexOfSpace > 0 ) { // if there's a space then this is an attribute of a joined table _soils
            let tableName = attrib.substr(0, indexOfSpace);
            let attributeName = attrib.substr(indexOfSpace + 1);
            let table = feature.get(tableName) || [];
            val = sampleYear.sampleBySampleYear(table, attributeName);
            // very similar to sampleYear.whichSample() below
            table.forEach(row => {
              if (row.sample_year == sampleYear.get()) {  // only soils has sample_year (not nematode)
                val = attributeName + '-' + row[attributeName];
                // val = row[attributeName];
              }
            });
          }

        // _nematode
        } else if (attrib.indexOf("_nematode") === 0) { // look for a default label _nematode
          rwg.layers.getActiveDrawLayers().forEach(function(lyrName) {
            let olLayerName = rwg.layers.getLayerByName(lyrName).olLyr;
            let gwmap = map.getLayerByTitle(olLayerName).get('gwmap');  // gwmap is a custom object attached to the ol layer
            if (gwmap && gwmap.defaultLabel) {
              let label_array = gwmap.defaultLabel.split('-');
              val = sampleYear.whichSample(feature, label_array[0], label_array[1]);  // sampleYear is a global function defined in cpc zones-conf.js
            }
          });
        }
      } else {
        val = feature.get(attrib);
      }
      if ((typeof val !== 'undefined' && val !== '' && val !== null)) {
        label += val + '\n';
      }
    });

    // how about an instance-level text style (new ol.style.Text) and then:
    //    textStyle.setText(label);
    //    return textStyle;
    // this would require that all the setters kept textStyle up to date

    // var offsetY = -((lines - 1) * parseInt(fontSize)); // offset is in pixels
    // var offsetY = -(lines * parseInt(fontSize)); // offset is in pixels
    style = new Text({
      overflow: overflow,
      // offsetY: offsetY,
      // textBaseline: 'top',  // 'middle' (must be lower case)
      // offsetX: offsetX,
      textAlign: 'center',  //  (must be lower case)
      // rotation: rotation,
      font: fontWeight + ' ' + fontSize + ' ' + fontFamily,
      // text: stringDivider(feature.getProperties()['label'], 16, '\n'),
      // text: (feature.get('label') || '') + '\n' + (feature.get('farm') || ''),
      text: label,
      fill: new Fill({color: fontColor}),
      stroke: parseInt(fontOutlineWidth) ? new Stroke({color: fontOutlineColor, width: parseInt(fontOutlineWidth)}) : null
    });
    return style;
  };

  /* called as each feature is rendered by ol */
  var styleFunc = function(feature, resolution) {
    let style;
    let featureType;
    let thematicFillColor;
    // setGeometryName(name);   // https://openlayers.org/en/latest/apidoc/ol.Feature.html#setGeometryName
    featureType = feature.getGeometry().getType();
    if (featureType.indexOf('Line') > -1) {
      style = [ new Style({
        stroke: new Stroke({
          color: strokeRGBA,
          width: parseInt(strokeWidth),
          lineDash: lineDash
        }),
        text: createTextStyle(feature, resolution)
      }) ];
    } else {
      if (rwg.config.thematic) {
        thematicFillColor = rwg.config.thematic.getThematicFillColor(feature);
      }
      style = [ new Style({
        fill: new Fill({
          color: thematicFillColor || fillRGBA
        }),
        stroke: new Stroke({
          color: outlineRGBA,
          width: parseInt(outlineWidth),
        }),
        image: new Circle({  // for selecting text objects
          radius: 15
          // , fill: new Fill({
          //   // color: 'rgba(255, 204, 51, 0.0)',  // '#ffcc33'
          //   color: fillRGBA
          // })
        }),
        text: createTextStyle(feature, resolution)
      }) ];
    }
    return style
  }

  return {
    styleFunc: styleFunc
  }

}

// export {createTextStyle, setStylesUI}
